import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { formatLedgerAmount } from "../manage/clientLedgerTypes";
import { getSettlementItemKey } from "./clientLedgerCalculations";

const warningMessages = {
  missingRate: "Brak stawki dla typu lekcji.",
  missingClient: "Nie mozna przypisac ucznia do klienta.",
};

const balanceClassName = value => (
  Number(value || 0) < 0 ? "text-danger font-weight-bold" : ""
);

const getSortIcon = (sortConfig, columnKey) => {
  let icon = "▲";
  if (sortConfig.key === columnKey) {
    icon = sortConfig.direction === "ascending" ? "▲" : "▼";
  }

  return (
    <span aria-hidden="true" style={sortConfig.key === columnKey ? undefined : { visibility: "hidden" }}>
      {icon}
    </span>
  );
};

const getSortButtonLabel = (label, sortConfig, columnKey) => {
  if (sortConfig.key === columnKey) {
    return `Sortuj po ${label} ${sortConfig.direction === "ascending" ? "malejaco" : "rosnaco"}`;
  }

  return `Sortuj po ${label} rosnaco`;
};

const renderSortableHeaderLabel = (label, shortLabel) => {
  if (!shortLabel) {
    return <span>{label}</span>;
  }

  return (
    <span className="settlements-header-label-responsive">
      <span className="settlements-header-label-long">{label}</span>
      <span className="settlements-header-label-short">{shortLabel}</span>
    </span>
  );
};

const renderSortableHeader = (label, shortLabel, columnKey, className, onSort, sortConfig) => (
  <div className={className}>
    <button
      type="button"
      className="btn btn-link settlements-sortable-column-button p-0 w-100"
      aria-label={getSortButtonLabel(label, sortConfig, columnKey)}
      title={label}
      onClick={() => onSort(columnKey)}
    >
      <span className="text-with-icon">
        {renderSortableHeaderLabel(label, shortLabel)} {getSortIcon(sortConfig, columnKey)}
      </span>
    </button>
  </div>
);

const renderLessonItem = ({ item, clientId, isExcluded, onToggle }) => (
  <div key={getSettlementItemKey(item)} className="manage-text text-secondary d-flex align-items-center">
    <input
      type="checkbox"
      checked={!isExcluded}
      className="mr-2 mt-1"
      style={{ transform: "scale(1.15)" }}
      onChange={() => onToggle(clientId, getSettlementItemKey(item))}
    />
    <div>
      <span className="mr-2">{item.date}</span>
      <span className="mr-2">{item.dayOfWeekName || ""}</span>
      <span className="mr-2">{item.startTime || ""}</span>
      <span className="mr-2">{item.classTypeName || item.classType || ""}</span>
      <span className="mr-2">{item.studentsPresent != null ? `${item.studentsPresent}/${item.studentsTotal}` : ""}</span>
      <span className="mr-2">{item.studentName || ""}</span>
      <span className="mr-2">{item.teacherName ? `nauczyciel: ${item.teacherName}` : ""}</span>
      <span className={item.isPresent === false ? "text-danger font-weight-bold" : ""}>
        {item.isPresent !== undefined && !item.isPresent ? "(nieobecny) " : ""}
      </span>
      <span className={item.reason === "autoDuplicate" ? "text-info mr-2" : "d-none"}>
        {item.reason === "autoDuplicate" ? "(auto-duplikat) " : ""}
      </span>
      <span>{formatLedgerAmount(item.amount)}</span>
    </div>
  </div>
);

function renderWarning(settlement, warning) {
  if (warning.reason === "duplicateConflict") {
    return (
      <div key={`${settlement.clientId}-${warning.duplicateKey}`} className="text-danger">
        {`Wykryto duplikat wymagajacy recznej decyzji: ${warning.date} ${warning.startTime || ""}, ${warning.studentName}.`}
      </div>
    );
  }

  return (
    <div key={`${settlement.clientId}-${warning.reason}-${warning.lessonRegisterId || warning.studentId || warning.classType || "warning"}`} className="text-danger">
      {warningMessages[warning.reason] || warning.reason}
    </div>
  );
}

const buildDefaultExpandedState = settlements => settlements.reduce((acc, settlement) => ({
  ...acc,
  [settlement.clientId]: !settlement.approved,
}), {});

function ClientSettlementsList({
  settlements,
  sortConfig,
  expansionResetKey,
  onSort,
  onToggleExclusion,
  onApprove,
  onOpenPayments,
}) {
  const [expandedClientIds, setExpandedClientIds] = useState(() => buildDefaultExpandedState(settlements));

  useEffect(() => {
    setExpandedClientIds(buildDefaultExpandedState(settlements));
  }, [expansionResetKey]);

  const toggleExpanded = clientId => {
    setExpandedClientIds(currentState => ({
      ...currentState,
      [clientId]: !currentState[clientId],
    }));
  };

  return (
    <div>
      <div className="row table-header manage-table-title text-center mx-0 settlements-table-header">
        {renderSortableHeader("Klient", null, "clientName", "col-md-3 table-cell settlements-sortable-column", onSort, sortConfig)}
        {renderSortableHeader("Saldo startowe", "Saldo S.", "openingBalance", "col-md-1 table-cell settlements-sortable-column", onSort, sortConfig)}
        {renderSortableHeader("Wplaty", null, "paymentsInMonth", "col-md-1 table-cell settlements-sortable-column", onSort, sortConfig)}
        {renderSortableHeader("Naliczenie", null, "monthlyChargeAmount", "col-md-2 table-cell settlements-sortable-column", onSort, sortConfig)}
        {renderSortableHeader("Saldo koncowe", null, "closingBalance", "col-md-2 table-cell settlements-sortable-column", onSort, sortConfig)}
        {renderSortableHeader("Status", null, "status", "col-md-1 table-cell settlements-sortable-column", onSort, sortConfig)}
        <div className="col-md-2 table-cell">Szczegoly</div>
      </div>
      {settlements.map((settlement, index) => (
        <div key={settlement.clientId} className={index % 2 === 0 ? "" : "table-row-alternate"}>
          <div className="table-row row d-flex manage-text text-center mx-0 align-items-center">
            <div className="col-md-3 table-cell">
              {settlement.clientId ? (
                <Link to={`/manage/client/${settlement.clientId}`}>{settlement.clientName}</Link>
              ) : (
                settlement.clientName
              )}
            </div>
            <div className="col-md-1 table-cell">{formatLedgerAmount(settlement.openingBalance)}</div>
            <div className="col-md-1 table-cell text-nowrap">
              <span className="mr-2">{formatLedgerAmount(settlement.paymentsInMonth)}</span>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 align-baseline"
                aria-label={`Zarzadzaj wplatami dla ${settlement.clientName}`}
                onClick={() => onOpenPayments(settlement.clientId, settlement.month)}
              >
                <i className="fas fa-receipt" aria-hidden="true" />
              </button>
            </div>
            <div className="col-md-2 table-cell">{formatLedgerAmount(settlement.summaryMonthlyChargeAmount)}</div>
            <div className={`col-md-2 table-cell ${balanceClassName(settlement.summaryClosingBalance)}`}>{formatLedgerAmount(settlement.summaryClosingBalance)}</div>
            <div className="col-md-1 table-cell">{settlement.statusLabel}</div>
            <div className="col-md-2 table-cell text-nowrap">
              <button type="button" className="btn btn-link p-0" onClick={() => toggleExpanded(settlement.clientId)}>
                {expandedClientIds[settlement.clientId] ? "Ukryj szczegoly" : "Pokaz szczegoly"}
              </button>
            </div>
          </div>
          {expandedClientIds[settlement.clientId] && (
            <div className="mx-0 mb-4 p-3 border manage-text">
              <div className="mb-3">
                {(settlement.previewSnapshot.warnings || []).map(warning => renderWarning(settlement, warning))}
                {(settlement.previewSnapshot.charged || []).length > 0 && (
                  <div className="mb-2">
                    <strong>Pozycje naliczone</strong>
                    {(settlement.previewSnapshot.charged || []).map(item => (
                      <React.Fragment key={getSettlementItemKey(item)}>
                        {renderLessonItem({ item, clientId: settlement.clientId, isExcluded: false, onToggle: onToggleExclusion })}
                      </React.Fragment>
                    ))}
                  </div>
                )}
                {(settlement.previewSnapshot.skipped || []).length > 0 && (
                  <div>
                    <strong>Pozycje pominiete</strong>
                    {(settlement.previewSnapshot.skipped || []).map(item => renderLessonItem({
                      item,
                      clientId: settlement.clientId,
                      isExcluded: true,
                      onToggle: onToggleExclusion,
                    }))}
                  </div>
                )}
              </div>
              <div className="mt-3 mb-3">
                <div>Suma robocza: {formatLedgerAmount(settlement.previewMonthlyChargeAmount)}</div>
                <div>Saldo po przeliczeniu: {formatLedgerAmount(settlement.previewClosingBalance)}</div>
              </div>
              <button type="button" className="btn btn-sm manage-button manage-red-button" onClick={() => onApprove(settlement.previewSettlement)}>
                {settlement.approved ? "Przelicz i zapisz ponownie" : "Zatwierdz"}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

ClientSettlementsList.propTypes = {
  settlements: PropTypes.array.isRequired,
  sortConfig: PropTypes.shape({
    key: PropTypes.string.isRequired,
    direction: PropTypes.string.isRequired,
  }).isRequired,
  expansionResetKey: PropTypes.string.isRequired,
  onSort: PropTypes.func.isRequired,
  onToggleExclusion: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  onOpenPayments: PropTypes.func.isRequired,
};

export default ClientSettlementsList;
